import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, Users, Rocket, Wrench, Sparkles, Send } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { BUSINESS, MEDIA } from "@/constants/business";
import { trackLead } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const perks = [
  { icon: <Rocket className="w-6 h-6" />, title: "Growth Across South India", desc: "Serve builders and clients across Karnataka, Andhra Pradesh, Telangana and Tamil Nadu — grow your career with the company." },
  { icon: <Wrench className="w-6 h-6" />, title: "Hands-On With World-Class CNC", desc: "Learn and operate 220-ton press-brakes, fiber-laser cutters, and robotic laser welding — skills that transfer anywhere." },
  { icon: <Users className="w-6 h-6" />, title: "Family of Engineers", desc: "Small, tight-knit team where every voice matters. Direct access to founders and senior engineers." },
  { icon: <Sparkles className="w-6 h-6" />, title: "Build Smart Elevators", desc: "Work on IoT-connected elevators with web-app monitoring — modern R&D meets industrial craft." },
];

export default function CareersPage() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", role_of_interest: "", experience_years: "", city: "", linkedin_url: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/career`, form);
      trackLead({ source: "careers", role: form.role_of_interest, city: form.city });
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please email us at " + BUSINESS.email);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Careers at Vila Elevators & Escalators | Join Our Team in Bangalore"
        description="Build your career at Vila Elevators & Escalators — Bangalore's leading in-house elevator manufacturer. Open roles across engineering, sales, service and manufacturing."
        path="/careers"
        keywords="elevator jobs bangalore, careers vila elevators, elevator engineer jobs, elevator technician jobs, hiring bangalore elevator company"
      />

      <PageHeader
        eyebrow="Join Us"
        title="Let's build a great future."
        desc="We're always looking for passionate engineers, technicians, designers and sales professionals to join Vila's growing team across South India."
        image={MEDIA.neonLift}
      />

      {/* Perks */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Why Vila</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              Build the future of vertical transportation with us.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((p) => (
              <div key={p.title} data-testid={`career-perk-${p.title.split(' ')[0].toLowerCase()}`} className="border border-[#e5ddd0] bg-[#faf7f2]/40 p-8 hover:border-[#b08d57]/50 transition-colors">
                <div className="w-12 h-12 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-5">{p.icon}</div>
                <h3 className="font-display text-lg font-bold text-[#1a1a1a] mb-2">{p.title}</h3>
                <p className="text-[#6b6b6b] text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values / What we look for */}
      <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">What We Value</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">People who care about their craft.</h2>
          <p className="text-[#6b6b6b] text-lg leading-relaxed max-w-2xl mx-auto">
            You don't need to be an elevator expert on day one. We hire for curiosity, ownership, and a bias for quality. If you love making things work — mechanically, electronically, or with customers — Vila is a place you'll thrive.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Apply Now</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-6">
              Tell us about yourself.
            </h2>
            <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">
              We review every application. If your background matches an active or upcoming opening, our team will reach out within 5-7 business days.
            </p>
            <p className="text-[#8a8a8a] text-sm">
              Prefer email? Send your resume to <a href={`mailto:${BUSINESS.email}`} className="text-[#b08d57] underline">{BUSINESS.email}</a> with subject "Career Application - [Your Name]".
            </p>
          </div>

          <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 p-8 lg:p-10">
            {success ? (
              <div data-testid="career-success" className="text-center py-6">
                <CheckCircle2 className="w-14 h-14 text-[#b08d57] mx-auto mb-4" />
                <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-3">Thank you, {form.name.split(" ")[0] || "candidate"}!</h3>
                <p className="text-[#6b6b6b] max-w-md mx-auto">
                  We've received your application. Our team will review it and get back to you if there's a match. In the meantime, feel free to email <a href={`mailto:${BUSINESS.email}`} className="text-[#b08d57]">{BUSINESS.email}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="career-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Your Name *" testid="career-name" value={form.name} onChange={update("name")} />
                  <Field label="Phone Number *" testid="career-phone" type="tel" value={form.phone} onChange={update("phone")} />
                  <Field label="Email" testid="career-email" type="email" value={form.email} onChange={update("email")} />
                  <Field label="City" testid="career-city" value={form.city} onChange={update("city")} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">Role of interest</label>
                    <select data-testid="career-role" value={form.role_of_interest} onChange={update("role_of_interest")} className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm">
                      <option value="">Select a role</option>
                      <option>Design / Mechanical Engineer</option>
                      <option>Electrical / Controls Engineer</option>
                      <option>Field Installation Technician</option>
                      <option>Service / AMC Technician</option>
                      <option>Sales Executive</option>
                      <option>CNC Operator</option>
                      <option>Project Manager</option>
                      <option>General / Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">Experience</label>
                    <select data-testid="career-exp" value={form.experience_years} onChange={update("experience_years")} className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm">
                      <option value="">Select</option>
                      <option>Fresher (0 yrs)</option>
                      <option>1-3 years</option>
                      <option>3-5 years</option>
                      <option>5-10 years</option>
                      <option>10+ years</option>
                    </select>
                  </div>
                </div>
                <Field label="LinkedIn Profile URL" testid="career-linkedin" value={form.linkedin_url} onChange={update("linkedin_url")} placeholder="https://linkedin.com/in/..." />
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">Tell us about yourself</label>
                  <textarea data-testid="career-message" value={form.message} onChange={update("message")} rows={4} placeholder="Your background, why Vila, any relevant projects..." className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm resize-none" />
                </div>
                {error && <div data-testid="career-error" className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3">{error}</div>}
                <button type="submit" data-testid="career-submit" disabled={submitting} className="w-full sm:w-auto bg-[#b08d57] hover:bg-[#8a6a3d] disabled:opacity-60 text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-3 transition-colors">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {submitting ? "Sending..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">{label}</label>
      <input {...props} data-testid={testid} className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm" />
    </div>
  );
}
